// src/components/common/RefundModal.js
// refund-modal-a1c47f9e -- Seam D Piece 3: the single, shared refund form.
//
// One modal, two homes (§5E of deposit-audit/seam-d-plan.md):
//   - an invoice payment row  (InvoiceDetailsView -> refundInvoicePayment)
//   - a paid/recorded deposit (QuoteDetailsView  -> refundDeposit)
//
// Card-vs-offline is a CHOICE INSIDE this modal, never a separate button. Card
// refunds are Piece 4 (the Stripe executor); until that lands the card option is
// present but not selectable, so it simply lights up when CARD_REFUNDS_ENABLED
// flips to true. Everything reachable here today is the offline record path.
//
// The caller owns the callable: onSubmit({ amount, offline, method }) issues the
// refund and RESOLVES on success / THROWS on failure. This modal owns the amount,
// the method, the over-refund cap (mirrors the server so the operator can't even
// try to exceed it), and the busy/error state.

import React, { useState, useEffect } from 'react';
import { X, Loader2, AlertTriangle, RotateCcw } from 'lucide-react';
import { formatCurrency } from '../../utils/formatters';
import { DEPOSIT_OFFLINE_METHODS } from '../../utils/quoteSchema';

// sdrefund5-1b6d8f30 -- Piece 5: the Stripe refund executor and its async
// settlement (webhook) have shipped, so card refunds are live. Flipped to true; the
// existing hasCardPayment logic below now lights the card option up whenever the
// selected payment / deposit carries a stripePaymentIntentId.
const CARD_REFUNDS_ENABLED = true;

const OFFLINE_METHOD_OPTIONS = [
    { value: DEPOSIT_OFFLINE_METHODS.ETRANSFER, label: 'E-transfer' },
    { value: DEPOSIT_OFFLINE_METHODS.CHEQUE, label: 'Cheque' },
    { value: DEPOSIT_OFFLINE_METHODS.CASH, label: 'Cash' },
    { value: DEPOSIT_OFFLINE_METHODS.OTHER, label: 'Other' },
];

const RefundModal = ({
    isOpen,
    onClose,
    title = 'Issue a refund',
    subjectLabel,
    refundableAmount,
    currencyCode = 'USD',
    hasCardPayment = false,
    warning = '', // d2refwarn-4b1f7a3c -- Seam D2: optional pre-refund banner, caller-supplied
    onSubmit,
}) => {
    const cap = Number(refundableAmount) || 0;
    // Card is selectable ONLY when the executor exists AND there is a card charge
    // to reverse. Today CARD_REFUNDS_ENABLED is false, so this is always false.
    const cardEligible = CARD_REFUNDS_ENABLED && !!hasCardPayment;

    const [choice, setChoice] = useState('offline'); // 'card' | 'offline'
    const [offlineMethod, setOfflineMethod] = useState(DEPOSIT_OFFLINE_METHODS.ETRANSFER);
    const [amount, setAmount] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    // Reset each time the modal opens: default the amount to the full refundable
    // figure and default the choice to card only when it is actually usable.
    useEffect(() => {
        if (!isOpen) return;
        setChoice(cardEligible ? 'card' : 'offline');
        setOfflineMethod(DEPOSIT_OFFLINE_METHODS.ETRANSFER);
        setAmount(cap > 0 ? cap.toFixed(2) : '');
        setSubmitting(false);
        setError('');
    }, [isOpen, cardEligible, cap]);

    if (!isOpen) return null;

    const amtNum = parseFloat(String(amount).replace(/[^0-9.]/g, '')) || 0;
    const amountInvalid = !(amtNum > 0) || amtNum > cap + 0.01;

    const handleConfirm = async () => {
        if (submitting || amountInvalid) return;
        const offline = choice !== 'card';
        if (!offline && !cardEligible) return; // card path not reachable pre-Piece 4
        setSubmitting(true);
        setError('');
        try {
            await onSubmit({
                amount: Number(amtNum.toFixed(2)),
                offline,
                method: offline ? offlineMethod : undefined,
            });
            onClose();
        } catch (e) {
            setError(e?.message || 'Could not issue the refund. Please try again.');
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white z-10">
                    <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                        <RotateCcw size={20} className="text-orange-600" /> {title}
                    </h2>
                    <button onClick={onClose} disabled={submitting} className="text-gray-500 hover:text-gray-700 disabled:opacity-50">
                        <X size={24} />
                    </button>
                </div>

                <div className="p-6 space-y-4">
                    {subjectLabel && (
                        <p className="text-sm text-gray-600">{subjectLabel}</p>
                    )}

                    {/* d2refwarn-4b1f7a3c -- Seam D2: prop-driven pre-refund warning. Rendered
                        ONLY when the caller passes `warning` (QuoteDetailsView, for a QB-synced
                        deposit with no refund-from account set). The invoice refund path never
                        passes it, so that path is untouched. Warns, does not block -- the Issue
                        refund button below stays enabled. */}
                    {warning && (
                        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
                            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                            <span>{warning}</span>
                        </div>
                    )}

                    {/* How the refund is issued: card vs offline, a choice not a button. */}
                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">How is the refund being made?</label>

                        <label className={`flex items-start gap-3 rounded-md border p-3 ${cardEligible ? 'cursor-pointer border-gray-300 hover:bg-gray-50' : 'border-gray-200 bg-gray-50 opacity-70 cursor-not-allowed'}`}>
                            <input
                                type="radio"
                                name="refund-choice"
                                className="mt-1"
                                checked={choice === 'card'}
                                disabled={!cardEligible}
                                onChange={() => setChoice('card')}
                            />
                            <span className="text-sm">
                                <span className="font-medium text-gray-900">Refund to card</span>
                                <span className="block text-xs text-gray-500">
                                    {!hasCardPayment
                                        ? "This payment wasn't taken by card, so there's nothing to reverse."
                                        : "The refund goes back to the customer's card — it can take a few business days to appear."}
                                </span>
                            </span>
                        </label>

                        <label className="flex items-start gap-3 rounded-md border border-gray-300 p-3 cursor-pointer hover:bg-gray-50">
                            <input
                                type="radio"
                                name="refund-choice"
                                className="mt-1"
                                checked={choice === 'offline'}
                                onChange={() => setChoice('offline')}
                            />
                            <span className="text-sm">
                                <span className="font-medium text-gray-900">Refund taken another way</span>
                                <span className="block text-xs text-gray-500">Cheque, cash, or e-transfer handed back to the customer.</span>
                            </span>
                        </label>
                    </div>

                    {choice === 'offline' && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">How was it refunded?</label>
                            <select
                                value={offlineMethod}
                                onChange={(e) => setOfflineMethod(e.target.value)}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#5F4EC9]"
                            >
                                {OFFLINE_METHOD_OPTIONS.map(o => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Refund amount</label>
                        <div className="relative rounded-md">
                            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                <span className="text-gray-500 text-sm">$</span>
                            </div>
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                className="block w-full rounded-md border border-gray-300 pl-7 py-2 text-sm focus:ring-[#5F4EC9] focus:border-[#5F4EC9]"
                            />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                            Up to {formatCurrency(cap, currencyCode)} can be refunded.
                        </p>
                        {amountInvalid && amount !== '' && (
                            <p className="text-xs text-red-500 mt-1">
                                Enter an amount greater than zero and no more than {formatCurrency(cap, currencyCode)}.
                            </p>
                        )}
                    </div>

                    {error && (
                        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}

                    <div className="flex gap-2 pt-2">
                        <button
                            onClick={onClose}
                            disabled={submitting}
                            className="flex-1 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleConfirm}
                            disabled={submitting || amountInvalid}
                            className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-md font-bold hover:bg-orange-700 disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                            {submitting ? <Loader2 size={16} className="animate-spin" /> : 'Issue refund'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RefundModal;
